// ============================================================================
// Spacing Visualization — Block-Level Overlay
// ============================================================================
//
// Renders 4 margin rects (orange) + 4 padding rects (green) + content outline
// (blue) + up to 8 SpacingLabels around the block.
//
// The overlay fills the parent (inset:0). Margin is the outermost band,
// padding is inside that, and the content area is the innermost region.
// This matches how BlockLayoutWrapper applies margin then padding INSIDE
// the EditorBlockWrapper.

import { Box } from '@chakra-ui/react';
import { SPACING_VIZ_COLORS } from './spacingVizConstants';
import { SpacingLabel } from './SpacingLabel';
import type { ResolvedSpacing, DirectionalTokenNames } from '../../utils/resolveSpacingToPx';

export interface SpacingOverlayProps {
  margin: ResolvedSpacing;
  padding: ResolvedSpacing;
  /** Original token names for margin sides — shown in label tooltips. */
  marginTokens?: DirectionalTokenNames;
  /** Original token names for padding sides — shown in label tooltips. */
  paddingTokens?: DirectionalTokenNames;
}

export function SpacingOverlay({ margin, padding, marginTokens, paddingTokens }: SpacingOverlayProps): React.ReactElement {
  const m = margin;
  const p = padding;

  return (
    <Box
      data-gc-testing-id="spacing-overlay"
      position="absolute"
      top={0}
      right={0}
      bottom={0}
      left={0}
      pointerEvents="none"
      zIndex={1}
    >
      {/* ---- Margin regions (orange) — outermost band ---- */}
      {/* Top margin */}
      <Box
        data-gc-testing-id="spacing-region-margin-top"
        position="absolute"
        top={0}
        left={0}
        right={0}
        height={`${m.top}px`}
        bg={SPACING_VIZ_COLORS.margin}
      >
        <SpacingLabel value={m.top} side="top" type="margin" tokenName={marginTokens?.top} />
      </Box>

      {/* Bottom margin */}
      <Box
        data-gc-testing-id="spacing-region-margin-bottom"
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        height={`${m.bottom}px`}
        bg={SPACING_VIZ_COLORS.margin}
      >
        <SpacingLabel value={m.bottom} side="bottom" type="margin" tokenName={marginTokens?.bottom} />
      </Box>

      {/* Left margin */}
      <Box
        data-gc-testing-id="spacing-region-margin-left"
        position="absolute"
        top={`${m.top}px`}
        bottom={`${m.bottom}px`}
        left={0}
        width={`${m.left}px`}
        bg={SPACING_VIZ_COLORS.margin}
      >
        <SpacingLabel value={m.left} side="left" type="margin" tokenName={marginTokens?.left} />
      </Box>

      {/* Right margin */}
      <Box
        data-gc-testing-id="spacing-region-margin-right"
        position="absolute"
        top={`${m.top}px`}
        bottom={`${m.bottom}px`}
        right={0}
        width={`${m.right}px`}
        bg={SPACING_VIZ_COLORS.margin}
      >
        <SpacingLabel value={m.right} side="right" type="margin" tokenName={marginTokens?.right} />
      </Box>

      {/* ---- Padding regions (green) — inset from margin ---- */}
      {/* Top padding */}
      <Box
        data-gc-testing-id="spacing-region-padding-top"
        position="absolute"
        top={`${m.top}px`}
        left={`${m.left}px`}
        right={`${m.right}px`}
        height={`${p.top}px`}
        bg={SPACING_VIZ_COLORS.padding}
      >
        <SpacingLabel value={p.top} side="top" type="padding" tokenName={paddingTokens?.top} />
      </Box>

      {/* Bottom padding */}
      <Box
        data-gc-testing-id="spacing-region-padding-bottom"
        position="absolute"
        bottom={`${m.bottom}px`}
        left={`${m.left}px`}
        right={`${m.right}px`}
        height={`${p.bottom}px`}
        bg={SPACING_VIZ_COLORS.padding}
      >
        <SpacingLabel value={p.bottom} side="bottom" type="padding" tokenName={paddingTokens?.bottom} />
      </Box>

      {/* Left padding */}
      <Box
        data-gc-testing-id="spacing-region-padding-left"
        position="absolute"
        top={`${m.top + p.top}px`}
        bottom={`${m.bottom + p.bottom}px`}
        left={`${m.left}px`}
        width={`${p.left}px`}
        bg={SPACING_VIZ_COLORS.padding}
      >
        <SpacingLabel value={p.left} side="left" type="padding" tokenName={paddingTokens?.left} />
      </Box>

      {/* Right padding */}
      <Box
        data-gc-testing-id="spacing-region-padding-right"
        position="absolute"
        top={`${m.top + p.top}px`}
        bottom={`${m.bottom + p.bottom}px`}
        right={`${m.right}px`}
        width={`${p.right}px`}
        bg={SPACING_VIZ_COLORS.padding}
      >
        <SpacingLabel value={p.right} side="right" type="padding" tokenName={paddingTokens?.right} />
      </Box>

      {/* ---- Content area outline (blue) ---- */}
      <Box
        data-gc-testing-id="spacing-region-content"
        position="absolute"
        top={`${m.top + p.top}px`}
        right={`${m.right + p.right}px`}
        bottom={`${m.bottom + p.bottom}px`}
        left={`${m.left + p.left}px`}
        border="1px dashed"
        borderColor={SPACING_VIZ_COLORS.content}
        borderRadius="2px"
      />
    </Box>
  );
}
